<template>
  <div>
    <FormComponent
      title="Edit Delivery Advice Form"
      :backRoute="backRoute"
      :nonFieldErrors="formErrors.non_field_errors"
    >
      <!-- Action Button -->
      <template #formActions>
        <v-btn
          color="primary"
          depressed
          :disabled="!allFormValidation || selectedContainers.length == 0"
          @click="submitForm"
        >
          Update
        </v-btn>
      </template>

      <template #formContent>
        <v-row>
          <v-col cols="12" lg="3">
            <div class="pb-2">
              <span class="text-h6"> Containers </span>

              <v-divider></v-divider>
            </div>

            <div class="Container-Height-Class">
              <div
                v-for="(container, i) in orderDetails.order_containers"
                :key="i"
              >
                <ContainerCard
                  :draggable="true"
                  :container="container"
                  @dragstart="onDragStart(container.id)"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" lg="9" class="DA-Form-Height-Class">
            <div class="pb-2">
              <span class="text-h6"> Selected Containers </span>
              <v-btn
                color="secondary"
                depressed
                @click="addContractPageRedirect()"
                class="ml-3 mb-3"
              >
                Add Tariff
              </v-btn>
              <v-divider></v-divider>
            </div>

            <div
              class="DropZone"
              dropzone
              @dragenter.prevent
              @dragover.prevent
              @drop="onDrop"
            >
              <div
                class="text-center DropZone-Label"
                v-if="selectedContainers.length == 0"
              >
                <span class="text-h6">Drag & Drop Containers Here</span>
              </div>

              <div v-else>
                <v-data-table
                  :headers="containerHeaders"
                  :items="selectedContainers"
                  class="elevation-4"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:[`item.actions`]="{ index }">
                    <v-icon small class="mr-2" @click="removeContainer(index)">
                      mdi-close
                    </v-icon>
                  </template>
                </v-data-table>
              </div>
            </div>

            <v-row class="mt-3">
              <v-col cols="12">
                <v-tabs v-model="tab" grow>
                  <v-tabs-slider color="white"></v-tabs-slider>
                  <v-tab> Basic Details </v-tab>
                  <v-tab> Document Upload </v-tab>
                </v-tabs>
              </v-col>

              <v-col cols="12">
                <v-tabs-items v-model="tab">
                  <v-tab-item>
                    <v-form ref="stepperForm1" v-model="valid1">
                      <v-row class="ma-0">
                        <v-col cols="12" class="py-0">
                          <span class="text-h6 font-weight-regular">
                            Date And Time
                          </span>
                        </v-col>

                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          class="Form-Columns"
                        >
                          <DateField
                            :inputProps="{
                              dense: true,
                              label: 'Movement Date *',
                              'hide-details': 'auto',
                              rules: [
                                (val) => !!val || 'Movement Date is Required!',
                              ],
                              'error-messages': formErrors.movement_date,
                            }"
                            required
                            :min="today"
                            v-model="daDetails.movement_date"
                            @input="delete formErrors.movement_date"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          class="Form-Columns"
                        >
                          <InputField
                            type="time"
                            label="Movement Time *"
                            required
                            :rules="[
                              (val) => !!val || 'Movement Time is Required!',
                            ]"
                            hide-details="auto"
                            v-model="daDetails.movement_time"
                            :error-messages="formErrors.movement_time"
                            @input="delete formErrors.movement_time"
                          />
                        </v-col>

                        <v-col cols="12" class="py-0">
                          <span class="text-h6 font-weight-regular">
                            Financial Details
                          </span>
                        </v-col>

                        <v-col
                          cols="12"
                          v-if="
                            orderDetails.customer &&
                            orderDetails.payment_term == 'Cash'
                          "
                          lg="4"
                          class="Form-Columns"
                        >
                          <MultiSelect
                            label="Payment Receipt *"
                            :items="paymentReceiptList"
                            itemText="title"
                            itemValue="id"
                            hide-details="auto"
                            v-model="daDetails.payment_receipt"
                            :rules="[
                              (v) => !!v || 'Payment Receipt is Required',
                            ]"
                            :error-messages="formErrors.payment_receipt"
                            @change="delete formErrors.payment_receipt"
                          />
                        </v-col>

                        <v-col
                          cols="12"
                          v-if="
                            orderDetails.customer &&
                            orderDetails.payment_term == 'Cash'
                          "
                          lg="4"
                          class="Form-Columns"
                        >
                          <v-btn
                            small
                            @click="showPaymentReceiptForm = true"
                            color="primary"
                            class="mr-2"
                            depressed
                          >
                            Add Payment Receipt
                          </v-btn>
                        </v-col>

                        <v-col cols="12" lg="4" class="Form-Columns">
                          <MultiSelect
                            label="Currency *"
                            :items="currencyList"
                            itemText="currency_code"
                            itemValue="currency_code"
                            :rules="[(val) => !!val || 'Currency is Required!']"
                            hide-details="auto"
                            v-model="daDetails.currency"
                            :error-messages="formErrors.currency"
                            @change="delete formErrors.currency"
                          ></MultiSelect>
                        </v-col>

                        <v-col cols="12" lg="4" class="Form-Columns">
                          <v-switch
                            :label="`Genset : ${daDetails.has_genset}`"
                            true-value="Yes"
                            false-value="No"
                            class="mt-1"
                            hide-details="auto"
                            v-model="daDetails.has_genset"
                            :error-messages="formErrors.has_genset"
                            @input="delete formErrors.has_genset"
                          />
                        </v-col>

                        <v-col cols="12" lg="4" class="Form-Columns">
                          <div class="d-flex justify-space-between">
                            <v-switch
                              :label="`Hazmat : ${daDetails.is_hazmat}`"
                              true-value="Yes"
                              false-value="No"
                              class="mt-1"
                              hide-details="auto"
                              v-model="daDetails.is_hazmat"
                              :error-messages="formErrors.is_hazmat"
                              @input="delete formErrors.is_hazmat"
                            />
                            <v-btn
                              icon
                              class="mr-3 red--text"
                              v-if="daDetails.is_hazmat == 'Yes'"
                              @click="showHazardousDialog = true"
                            >
                              <v-icon>mdi-alert</v-icon>
                            </v-btn>
                          </div>
                        </v-col>

                        

                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          class="Form-Columns"
                        >
                          <MultiSelect
                            label="Select Branch *"
                            :items="allBranchList"
                            itemText="branch_title"
                            itemValue="id"
                            :rules="[(val) => !!val || 'Branch is Required!']"
                            hide-details="auto"
                            v-model="daDetails.branch"
                            :error-messages="formErrors.branch"
                            @input="delete formErrors.branch"
                          ></MultiSelect>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          class="Form-Columns"
                        >
                          <MultiSelect
                            label="Select Depot *"
                            :items="allDepotList"
                            itemText="depot_name"
                            itemValue="id"
                            :rules="[(val) => !!val || 'Depot is Required!']"
                            hide-details="auto"
                            v-model="daDetails.depot"
                            :error-messages="formErrors.depot"
                            @input="delete formErrors.depot"
                          ></MultiSelect>
                        </v-col>

                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="2"
                          class="Form-Columns"
                        >
                          <InputField
                            type="text"
                            label="Purchase Order"
                            hide-details="auto"
                            v-model="daDetails.po_no"
                            :error-messages="formErrors.po_no"
                            @input="delete formErrors.po_no"
                          />
                        </v-col>

                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="2"
                          class="Form-Columns"
                        >
                          <InputField
                            type="text"
                            label="Shipment Number"
                            hide-details="auto"
                            v-model="daDetails.shipment_no"
                            :error-messages="formErrors.shipment_no"
                            @input="delete formErrors.shipment_no"
                          />
                        </v-col>

                        <v-col
                          cols="12"
                          sm="6"
                          md="4"
                          lg="2"
                          class="Form-Columns"
                        >
                          <InputField
                            type="text"
                            label="Vessel Number"
                            hide-details="auto"
                            v-model="daDetails.vessel_no"
                            :error-messages="formErrors.vessel_no"
                            @input="delete formErrors.vessel_no"
                          />
                        </v-col>

                        <v-col cols="12" class="Form-Columns">
                          <MultiSelect
                            label="Select Milestones"
                            :multiple="true"
                            :chips="true"
                            :showAll="true"
                            :items="milestoneList"
                            itemText="milestone_name"
                            itemValue="id"
                            hide-details="auto"
                            v-model="daDetails.milestones"
                            :error-messages="formErrors.milestones"
                            @change="delete formErrors.milestones"
                          >
                          </MultiSelect>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-tab-item>

                  <v-tab-item>
                    <v-form ref="stepperForm2" v-model="valid2">
                      <v-row class="ma-0">
                        <v-col cols="12" class="py-0">
                          <span class="text-h6 font-weight-regular">
                            Upload Documents
                          </span>
                        </v-col>

                        <v-col cols="12" sm="6" md="4" class="Form-Columns">
                          <InputField
                            type="text"
                            v-model="document_type"
                            label="Document Type"
                            :error-messages="formErrors.document_type"
                            @input="delete formErrors.document_type"
                          />
                        </v-col>

                        <v-col cols="12" sm="6" md="4" class="Form-Columns">
                          <v-file-input
                            v-model="document"
                            label="Document"
                            :rules="[
                              (file) =>
                                !file ||
                                file.size < 2e6 ||
                                'File size should be less than 2 MB!',
                            ]"
                            accept="image/x-png,image/jpg,image/jpeg,application/pdf"
                            outlined
                            :error-messages="formErrors.document"
                            @input="delete formErrors.document"
                            dense
                          />
                        </v-col>

                        <v-col cols="12" sm="6" md="4">
                          <v-btn
                            color="primary"
                            depressed
                            :disabled="!document || !document_type"
                            @click="addDocument"
                            >Add</v-btn
                          >
                        </v-col>

                        <v-col cols="12">
                          <div
                            class="mt-0 mb-4"
                            v-show="
                              daDetails.da_documents &&
                              daDetails.da_documents.length
                            "
                          >
                            <h3 class="Form-Heading">Uploaded</h3>

                            <v-card
                              elevation="0"
                              outlined
                              v-for="(data, i) in daDetails.da_documents"
                              :key="i"
                            >
                              <v-card-text>
                                <v-row>
                                  <v-col cols="4">
                                    <a :href="data.document" target="_black">
                                      <h4>{{ data.document_type }}</h4>
                                    </a>
                                  </v-col>
                                  <v-col>
                                    Created on :
                                    {{ data.created | formatDate(true) }}
                                  </v-col>
                                  <v-col>
                                    Added By :
                                    {{ data.added_by }}
                                  </v-col>
                                  <v-col class="d-flex justify-end">
                                    <v-icon
                                      color="red"
                                      @click="deleteDocument(data, i)"
                                      >mdi-delete</v-icon
                                    >
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </div>

                          <div v-show="daDocuments && daDocuments.length">
                            <h3 class="Form-Heading">New Upload</h3>
                          </div>

                          <v-card
                            elevation="0"
                            outlined
                            v-for="(data, j) in daDocuments"
                            :key="j"
                          >
                            <v-card-text>
                              <v-row>
                                <v-col cols="3">
                                  <a
                                    :href="generateUrl(data.document)"
                                    target="_black"
                                  >
                                    <h4>{{ data.document_type }}</h4>
                                  </a>
                                </v-col>
                                <v-col cols="4">
                                  <a
                                    :href="generateUrl(data.document)"
                                    target="_black"
                                  >
                                    <h4>{{ data.document.name }}</h4>
                                  </a>
                                </v-col>
                                <v-col cols="2">
                                  <h4>{{ getSize(data.document.size) }}</h4>
                                </v-col>
                                <v-col cols="2"> </v-col>
                                <v-col cols="1">
                                  <v-icon
                                    small
                                    color="primary"
                                    @click="removeFile(j)"
                                    >mdi-close</v-icon
                                  >
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </FormComponent>
    <AdminDAHazardousForm
      v-model="showHazardousDialog"
      dialogTitle="DA Hazardous Cargo"
      :daHazardousFormDetails="daHazardousFormDetails"
      @saveDAHazardousForm="saveDAHazardousForm"
    />
    <PaymentReceiptForm
      v-model="showPaymentReceiptForm"
      :companyId="orderDetails.customer"
      @refreshList="getPaymentReceiptList"
    />
  </div>
</template>

<script>
import FormComponent from "@/components/General/FormComponent.vue";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import Tooltip from "@/components/General/Tooltip.vue";
import ContainerCard from "@/components/General/ContainerCard.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import AdminDAHazardousForm from "@/components/ModuleBased/dialogForms/AdminDAHazardousForm.vue";
import PaymentReceiptForm from "@/components/ModuleBased/dialogForms/PaymentReceiptForm.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    FormComponent,
    InputField,
    MultiSelect,
    Tooltip,
    ContainerCard,
    DateField,
    AdminDAHazardousForm,
    PaymentReceiptForm,
  },

  data() {
    return {
      showPaymentReceiptForm: false,
      showHazardousDialog: false,
      paymentReceiptList: [],
      hazardousId: null,
      daHazardousFormDetails: {},

      tab: 0,
      // docs vars
      document_type: null,
      document: null,
      allDepotList: [],
      daDocuments: [],
      allBranchList: [],
      allInternalTransporterList: [],

      paymentTerms: [],
      currencyList: [],
      milestoneList: [],

      selectedContainers: [],
      containerHeaders: [
        {
          text: "Container No.",
          value: "container_no",
        },
        {
          text: "Container Type",
          value: "container_type_title",
        },
        {
          text: "Source Address",
          value: "source_address",
        },
        {
          text: "Mid Point Address",
          value: "mid_address",
        },
        {
          text: "Destination Address",
          value: "destination_address",
        },
        {
          text: "Movement Date",
          value: "movement_date",
        },
        {
          text: "Movement Time",
          value: "movement_time",
        },
        {
          text: "Actions",
          value: "actions",
        },
      ],

      daDetails: {
        has_genset: "No",
        is_hazmat: "No",
        milestones: [],
      },
      orderDetails: {},
      valid1: true,
      valid2: true,
      formErrors: {},

      // drag & drop vars
      draggedId: null,
      today: new Date().toISOString().slice(0, 10),

      backRoute: { path: "/app/admin/order/list" },
    };
  },

  computed: {
    allFormValidation() {
      return this.valid1 && this.valid2;
    },
  },

  methods: {
    getPaymentReceiptList(params = {}) {
      if (this.orderDetails && this.orderDetails.customer) {
        params = {
          ...params,
          da: this.daDetails.id,
          company: this.orderDetails.customer,
        };

        this.$api.deliveryAdvice
          .paymentReceiptsList(params)
          .then((res) => {
            this.paymentReceiptList = res.data;
          })
          .catch((err) => {
            this.$bus.$emit("showLoader", false);
            this.$bus.$emit("showToastMessage", {
              message: "Couldn't Fetch delivery charge list!",
              color: "error",
            });
            this.formErrors = err.data;
          });
      }
    },
    saveDAHazardousForm(data) {
      this.daHazardousFormDetails = { ...data };
    },
    addDAHazardousCargo(daID) {
      let payload = { ...this.daHazardousFormDetails, delivery_advice: daID };
      this.$api.daHazardousCargo
        .addDAHazardousCargo(payload)
        .then(() => {})
        .catch((err) => {
          console.log(err);
        });
    },
    updateDAHazardousCargo() {
      this.$api.daHazardousCargo
        .updateDAHazardousCargo({
          id: this.hazardousId,
          data: this.daHazardousFormDetails,
        })
        .then((res) => {})
        .catch((err) => {
          console.log(err);
        });
    },
    getdaHazardousCargoDetails() {
      if (this.hazardousId) {
        this.editMode = true;
        this.$bus.$emit("showLoader", true);
        this.$api.daHazardousCargo
          .getdaHazardousCargoDetails(this.hazardousId)
          .then((res) => {
            this.$bus.$emit("showLoader", false);
            this.daHazardousFormDetails = res.data;
          })
          .catch((err) => {
            console.error(err);
            this.$bus.$emit("showLoader", false);
          });
      }
    },
    addContractPageRedirect() {
      const path = {
        path: "/app/admin/customer-contract/add",
      };
      path.query = {
        backRoute: this.$route.path,
        da_id: this.$route.query.da_id,
      };
      this.$router.push(path);
    },
    callBackRoute() {
      this.$router.push(this.backRoute);
    },
    deleteDocument(document, index) {
      if (
        confirm(`Are you sure you want to delete ${document.document_type} ?`)
      ) {
        this.$bus.$emit("showLoader", true);
        this.$api.deliveryAdvice
          .deleteDeliveryAdviceDocument(document.id)
          .then((res) => {
            this.$bus.$emit("showToastMessage", {
              message: "Document deleted successfully!",
              color: "success",
            });
            this.daDetails.da_documents.splice(index, 1);
            this.$bus.$emit("showLoader", false);
          })
          .catch((err) => {
            console.error(err);
            this.$bus.$emit("showToastMessage", {
              message: "Couldn't delete document!",
              color: "error",
            });
            this.$bus.$emit("showLoader", false);
          });
      }
    },
    addDocument() {
      this.daDocuments.push({
        document_type: this.document_type,
        document: this.document,
      });
      this.document = null;
      this.document_type = null;
    },
    removeFile(index) {
      this.daDocuments.splice(index, 1);
    },
    generateUrl(file) {
      return URL.createObjectURL(file);
    },
    getSize(bytes) {
      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "0 Byte";
      var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },
    async uploadDADocuments(id) {
      let i = 0;
      while (i < this.daDocuments.length) {
        let payload = {
          id: id,
          data: null,
        };
        let newForm = new FormData();
        newForm.append("document_type", this.daDocuments[i].document_type);
        newForm.append("document", this.daDocuments[i].document);
        payload.data = newForm;
        await this.$api.deliveryAdvice.uploadDeliveryAdviceDocument(payload);
        i++;
      }
    },
    removeContainer(index) {
      let order = this.selectedContainers.splice(index, 1)[0];
      if (order && "oldIndex" in order) {
        this.orderDetails.order_containers.splice(order.oldIndex, 0, order);
      } else {
        this.orderDetails.order_containers.splice(
          this.orderDetails.order_containers.length,
          0,
          order
        );
      }
    },
    onDragStart(id) {
      this.draggedId = id;
    },
    onDrop() {
      let index = this.orderDetails.order_containers.indexOf(
        this.orderDetails.order_containers.find((v) => v.id == this.draggedId)
      );
      if (~index) {
        let order = this.orderDetails.order_containers.splice(index, 1, {})[0];
        order.oldIndex = index;
        if (this.selectedContainers.length < 5) {
          this.selectedContainers.push(order);
        }
      }
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      let formData = {
        order_containers: this.selectedContainers.map((obj) => obj.id),
        order: this.daDetails.order,
        customer: this.daDetails.customer,
        boe: this.daDetails.boe,
        has_genset: this.daDetails.has_genset,
        is_hazmat: this.daDetails.is_hazmat,
        movement_date: this.daDetails.movement_date,
        depot: this.daDetails.depot,
        branch: this.daDetails.branch,
        movement_time: this.daDetails.movement_time,
        milestones: this.daDetails.milestones,
        ...this.daDetails,
      };
      let payload = {
        id: this.daDetails.id,
        data: formData,
      };
      this.$api.deliveryAdvice
        .updateDeliveryAdvice(payload)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          if (this.daDocuments.length > 0) {
            this.uploadDADocuments(res.data.id);
          }
          if (this.daDocuments.length > 0) {
            this.updateDAHazardousCargo(res.data.id);
          }
          if (
            formData &&
            formData.is_hazmat == "Yes" &&
            this.daHazardousFormDetails
          ) {
            if (this.daDetails && this.daDetails.da_hazardous_cargo_id) {
              this.updateDAHazardousCargo();
            } else {
              this.addDAHazardousCargo(this.daDetails.id);
            }
          }
          this.$bus.$emit("showToastMessage", {
            message: `Delivery Advice ${res.data.da_no} updated successfully!`,
            color: "success",
          });

          this.$router.push(this.backRoute);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);

          this.formErrors = { ...this.formErrors, ...err.data };
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't update Delivery Advice!",
            color: "error",
          });
        });
    },

    async getDeliveryAdviceObject(id) {
      this.$bus.$emit("showLoader", true);
      await this.$api.deliveryAdvice
        .getDeliveryAdviceObject(id)
        .then(async (res) => {
          this.daDetails = res.data;
          this.selectedContainers = this.daDetails.delivery_advice_containers;
          this.hazardousId = this.daDetails.da_hazardous_cargo_id;
          this.getdaHazardousCargoDetails();

          if (!this.daDetails.milestones) {
            this.daDetails.milestones = [];
          }
          this.getMilestoneList();

          await this.getOrderObject(this.daDetails.order);
          this.$bus.$emit("showLoader", false);
          this.setBackRoute();
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    async getOrderObject(id) {
      this.$bus.$emit("showLoader", true);
      await this.$api.customerOrder
        .getCustomerOrderObject(id)
        .then((res) => {
          this.orderDetails = res.data;
          this.orderDetails.order_containers = res.data.order_containers.filter(
            (obj) => {
              if (!obj.assigned_to_da) {
                return obj;
              }
            }
          );
          this.getPaymentReceiptList();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    setBackRoute() {
      if ("backRoute" in this.$route.query) {
        this.backRoute.path = this.$route.query.backRoute;
      }
      if ("da_id" in this.$route.query) {
        if (!this.backRoute.query) {
          this.backRoute.query = {};
        }
        this.backRoute.query.da_id = this.$route.query.da_id;
      }
    },
    getDeliveryAdviceOptions() {
      this.$bus.$emit("showLoader", true);
      this.$api.deliveryAdvice
        .getDeliveryAdviceOptions()
        .then((res) => {
          this.paymentTerms = res.data.actions.POST.payment_term.choices.map(
            (obj) => obj.value
          );
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getCurrencyList() {
      this.$bus.$emit("showLoader", true);
      this.$api.currency
        .getCurrencyList({ limit: "all" })
        .then((res) => {
          this.currencyList = res.data;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getDepotList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        limit: "all",
        ...params,
      };
      this.$api.depot
        .getDepotList(params)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.allDepotList = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getBranchList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        limit: "all",
        ...params,
      };
      this.$api.branch
        .getBranchList(params)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.allBranchList = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getInternalTransporterList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        company_type: "Transporter",
        is_internal_company: true,
        limit: "all",
        ...params,
      };
      this.$api.company
        .getCompanyList(params)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.allInternalTransporterList = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getMilestoneList() {
      const params = {
        job_type: this.selectedContainers[0].job_type,
      };
      this.$api.milestone
        .getMilestoneList(params)
        .then((res) => {
          this.milestoneList = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },

  async beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("delivery_advice");
      if (!permissions || !permissions.change) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.getDeliveryAdviceOptions();
        this.getCurrencyList();
        this.getInternalTransporterList();
        this.getDepotList();
        this.getBranchList();
        await this.getDeliveryAdviceObject(this.$route.params.id);
      }
    }
  },
};
</script>

<style scoped>
.Container-Height-Class {
  height: calc(100vh - 262px);
  overflow-y: auto;
}

.DA-Form-Height-Class {
  height: calc(100vh - 197px);
  overflow-y: auto;
}

.DropZone {
  min-height: 250px;
  max-height: 300px;
  overflow-y: auto;
  padding: 15px;
  padding-bottom: 25px;
  border-style: groove;
  border-color: #fafafa;
  background-color: #e0e0e0;
}
</style>
